import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import styled from "styled-components";

import Checkbox from "components/atoms/Checkbox";
import Alert from "components/atoms/alert/Alert";
import useLoading from "components/atoms/loading/useLoading";
import GeneralText from "components/atoms/text/GeneralText";
import StyledText from "components/atoms/text/StyledText";

import AbstractModal from "components/page/modal/AbstractModal";
import AbstractModalFooter from "components/page/modal/AbstractModalFooter";
import CouponRegisterModal from "components/page/modal/CouponRegisterModal";

import {
    usePaymentBillingKeyCreate,
    usePaymentBillingKeyDelete,
    usePaymentCoupon,
    usePaymentCouponUse,
    usePaymentDiscountPolicy,
    usePaymentPlan,
    usePaymentPlanSubscriptionHistoryExtend,
} from "hooks/queries/usePayment";

import Constants from "utils/constants";

import moment from "moment";

const Container = styled.div`
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
`;

const SubInfoBox = styled.div`
    margin-top: 40px;
    padding: 8px 16px;
    width: 100%;
    background-color: var(--color-InfoBox);
    align-items: center;
    border-radius: 4px;
`;

const InfoListWrap = styled.ul`
    margin: 0;
    padding: 0 0 0 16px;
`;

const SubInfoItem = styled.li`
    margin: 4px 0;
    line-height: 1.4;
    text-align: justify;
    color: var(--color-SubBlack);
`;

const Wrapper = styled.div`
    margin: 20px 0 0 0px;
`;

const OptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Option = styled.label`
    display: block;
    align-items: center;
    margin-bottom: 15px;
    padding: 16px;
    border-radius: 4px;
    cursor: pointer;
    border: ${(props) => (props.selected ? "1px solid var(--color-Button1)" : "1px solid #ccc")};

    &:last-child {
        margin-bottom: 0;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
`;

const RadioButton = styled.input`
    margin-right: 10px;
    accent-color: var(--color-Key);
`;

const OptionText = styled(StyledText)`
    font-size: 1.4rem;
`;

const Description = styled(StyledText)`
    font-size: 1.2rem;
    color: var(--color-SubBlack);
    margin-top: 10px;
`;

const OptionTitleText = styled(StyledText)`
    font-size: 1.4rem;
    font-weight: 500;
    margin: 40px 0 20px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const contentStyle = {
    size: "regular",
    color: "var(--color-Black)",
};

const listStyle = {
    size: "small",
    textAlign: "justify",
};

const PlanInfoArea = styled.div`
    text-align: center;
`;

const PlanSubText = styled(StyledText)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-top: 12px;
`;

const PaymentMethodText = styled(StyledText)`
    font-size: 1.4rem;
    color: var(--color-Black);
`;

const CouponRegisterText = styled(StyledText)`
    font-size: 1.2rem;
    font-weight: 400;
    margin-left: auto;
    color: var(--color-Grey1);
`;

const CouponList = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
`;

const CouponItem = styled.label`
    display: flex;
    align-items: flex-start;
    padding: 12px;
    background-color: var(--color-InfoBox);
    cursor: pointer;

    &:not(:first-child) {
        margin-top: 2px;
    }
`;

const CouponContent = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 0px;
`;

const NoCouponText = styled(StyledText).attrs({
    fontSize: "small",
    color: "var(--color-SubBlack)",
})``;

const CardOptionWrapper = styled(Option)`
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
`;

const CardTermSelector = styled.div`
    display: flex;
    align-items: center;
    margin: 16px 0;
`;

const TermOption = styled.label`
    display: flex;
    align-items: center;
    padding: 8px 12px;
    margin-right: 12px;
    border-radius: 4px;
    height: 36px;
    border: ${(p) => (p.selected ? "1px solid var(--color-Button1)" : "1px solid var(--color-Line)")};
    cursor: pointer;
`;

const DiscountBadge = styled(StyledText)`
    font-size: 1rem;
    font-weight: 500;
    color: white;
    background-color: var(--color-Button1);
    border-radius: 4px;
    padding: 2px 6px;
    margin-left: 8px;
`;

const CardInfoBox = styled(SubInfoBox)`
    margin-top: 0;
    padding: 16px;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid var(--color-Line);
    margin: 12px 0;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 16px;
    width: 100%;
`;

const FeeSubText = styled(StyledText)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-Grey1);
`;

function PaymentExtendSubscribeModal(props) {
    // props
    // user: user 정보
    // exitModal: 모달 닫기 함수
    // isGroupPlan: 그룹 플랜 여부
    const navigate = useNavigate();
    const [paymentMethod, setPaymentMethod] = useState("card");

    // 쿠폰
    const couponUseMutation = usePaymentCouponUse();
    const couponQuery = usePaymentCoupon();

    const [couponList, setCouponList] = useState([]);
    const [showCouponRegisterModal, setShowCouponRegisterModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    const [cardTerm, setCardTerm] = useState("1m"); // '1m' or '3m'

    const discountPolicyQuery = usePaymentDiscountPolicy();
    const [longTermDiscounPolicy, setLongTermDiscountPolicy] = useState({});
    const [groupDiscountPolicy, setGroupDiscountPolicy] = useState({});

    const paymentPlanQuery = usePaymentPlan();
    const [plan, setPlan] = useState({});

    // 자동 연장 여부
    const [autoExtend, setAutoExtend] = useState(true);

    const extendSubscribeHistoryMutation = usePaymentPlanSubscriptionHistoryExtend();
    const billingKeyMutation = usePaymentBillingKeyCreate();
    const billingKeyDelete = usePaymentBillingKeyDelete();

    const { setLoading } = useLoading();

    useEffect(() => {
        if (couponQuery.data) {
            if (!props.isGroupPlan) {
                const filteredCoupons = couponQuery.data.filter((coupon) => !coupon.used_at);
                setCouponList(filteredCoupons.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
            }
        }

        if (discountPolicyQuery.data) {
            if (props.isGroupPlan) {
                const groupPolicy = discountPolicyQuery.data.find(
                    (policy) => policy.internal_code === Constants.GROUP_DISCOUNT_POLICY,
                );
                setGroupDiscountPolicy(groupPolicy);
            }
            const longTermPolicy = discountPolicyQuery.data.find(
                (policy) => policy.internal_code === Constants.THREE_MONTH_DISCOUNT_POLICY,
            );
            setLongTermDiscountPolicy(longTermPolicy);
        }

        if (paymentPlanQuery.data) {
            if (props.isGroupPlan) {
                setPlan(paymentPlanQuery.data.find((plan) => plan.id === Constants.GROUP_PLAN));
            } else {
                setPlan(paymentPlanQuery.data.find((plan) => plan.id === Constants.PAID_PLAN));
            }
        }
    }, [couponQuery.data, discountPolicyQuery.data, paymentPlanQuery.data]);

    useEffect(() => {
        if (!props.isGroupPlan) {
            setPaymentMethod("card");
            if (couponList.length > 0) {
                setPaymentMethod("coupon");
                const latestCoupon = couponList.reduce((prev, current) =>
                    new Date(prev.updated_at) > new Date(current.updated_at) ? prev : current,
                );
                setSelectedCoupon(latestCoupon.number);
            } else if (props.isOnlyCouponUse) {
                setPaymentMethod("coupon");
            } else {
                setPaymentMethod("card");
            }
        }
    }, [couponList]);

    useEffect(() => {
        const jquery = document.createElement("script");
        jquery.src = "https://code.jquery.com/jquery-1.12.4.min.js";
        const iamport = document.createElement("script");
        iamport.src = "https://cdn.iamport.kr/js/iamport.payment-1.1.8.js";
        document.head.appendChild(jquery);
        document.head.appendChild(iamport);

        return () => {
            document.head.removeChild(jquery);
            document.head.removeChild(iamport);
        };
    }, []);

    const extendSubscription = () => {
        const { IMP } = window;
        IMP.init("imp65161342");
        let randomNumber = Math.floor(Math.random() * 90000) + 10000;
        let merchant_uid = `extend_${props.user.email.split("@")[0]}_${randomNumber}`;
        let customer_uid = `${props.user.email.split("@")[0]}_${randomNumber}`;

        const amount = paymentAmount;

        const callback = (response) => {
            document.getElementById("iamport-modal").style.display = "none";

            if (response.success) {
                setLoading(true, "결제 요청 중입니다.");
                billingKeyMutation.mutate(response, {
                    onSuccess: (billingKey) => {
                        extendSubscribeHistoryMutation.mutate(
                            {
                                id: props.subscription.id,
                                merchant_uid: merchant_uid,
                                billing_key: billingKey.id,
                                amount: amount,
                                discount_policy: [
                                    cardTerm === "3m" && longTermDiscounPolicy.id,
                                    props.isGroupPlan && groupDiscountPolicy.id,
                                ],
                                is_scheduled: autoExtend,

                                // props.isGroupPlan 에따라 group 정보 추가
                                ...(props.isGroupPlan && {
                                    group: props.groupInfo,
                                }),
                            },
                            {
                                onSuccess: () => {
                                    setLoading(false);
                                    Alert("info", "구독", "요금제의 이용 기간이 연장되었습니다.");
                                    // refreshUserPlan();
                                    if (!autoExtend) {
                                        billingKeyDelete.mutate(billingKey.id);
                                    }
                                    props.exitModal();
                                },
                                onError: () => {
                                    setLoading(false);
                                    Alert(
                                        "warn",
                                        "에러",
                                        "구독 정보 변경 중 오류가 발생했습니다. 관리자에게 문의해주세요.",
                                    );
                                },
                            },
                        );
                    },
                    onError: () => {
                        setLoading(false);
                        Alert("warn", "에러", "결제 정보 등록 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
                    },
                });
            } else {
                if (response.error_msg === "사용자가 결제를 취소하셨습니다") {
                    return;
                }
                Alert("warn", "에러", "결제 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
            }
        };
        document.getElementById("iamport-modal").style.display = "block";
        const from = moment(props.subscription.ended_at).format("YYYY-MM-DD");
        const to = moment(from)
            .add(cardTerm === "3m" ? 3 : 1, "months")
            .add(-1, "days")
            .format("YYYY-MM-DD");
        IMP.request_pay(
            {
                pg: `${process.env.REACT_APP_ENV !== "prd" ? "danal_tpay.9810030929" : "danal_tpay"}`,
                pay_method: "card",
                merchant_uid: merchant_uid,
                name: `${plan.name} > ${autoExtend ? "정기 결제" : "일반 결제"}`,
                period: {
                    from: from,
                    to: to,
                },
                amount: amount,
                customer_uid: customer_uid,
                buyer_email: props.user.email,
            },
            callback,
        );
    };

    const planAmount = plan.price;

    const monthlyAmount =
        planAmount -
        ((cardTerm === "3m" ? (planAmount * longTermDiscounPolicy.discount_rate) / 100 : 0) +
            (props.isGroupPlan ? (planAmount * groupDiscountPolicy.discount_rate) / 100 : 0));

    const paymentAmount =
        monthlyAmount * (cardTerm === "3m" ? 3 : 1) * (props.isGroupPlan ? props.groupInfo.max_member : 1);

    return (
        <Container>
            <PlanInfoArea>
                <PlanSubText>현재 사용 중인 요금제의 이용 기간을 연장합니다.</PlanSubText>
            </PlanInfoArea>
            <OptionTitleText>결제 옵션</OptionTitleText>
            <Wrapper>
                <OptionWrapper>
                    {/* 쿠폰 뷰 */}
                    {!props.isGroupPlan && (
                        <Option selected={paymentMethod === "coupon"}>
                            <HeaderRow onClick={() => setPaymentMethod("coupon")}>
                                <PaymentMethodText>쿠폰 결제</PaymentMethodText>
                                <CouponRegisterText
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowCouponRegisterModal(true);
                                    }}
                                >
                                    쿠폰 등록
                                </CouponRegisterText>
                            </HeaderRow>
                            {paymentMethod === "coupon" && (
                                <CouponList>
                                    {couponList.length === 0 ? (
                                        <NoCouponText>사용 가능한 쿠폰이 없습니다.</NoCouponText>
                                    ) : (
                                        couponList.map((coupon) => (
                                            <CouponItem
                                                key={coupon.id}
                                                selected={selectedCoupon === coupon.number}
                                                onClick={() => setSelectedCoupon(coupon.number)}
                                            >
                                                <div style={{ marginTop: "4px" }}>
                                                    <RadioButton
                                                        type="radio"
                                                        name="couponOption"
                                                        checked={selectedCoupon === coupon.number}
                                                        onChange={() => setSelectedCoupon(coupon.number)}
                                                    />
                                                </div>
                                                <CouponContent>
                                                    <OptionText>{coupon.name}</OptionText>
                                                    <Description>쿠폰 번호: {coupon.number}</Description>
                                                    <Description>
                                                        사용 기한: {moment(coupon.expired_at).format("YYYY.MM.DD")}
                                                    </Description>
                                                </CouponContent>
                                            </CouponItem>
                                        ))
                                    )}
                                </CouponList>
                            )}
                        </Option>
                    )}
                    {/* 카드 뷰 */}
                    {!props.isOnlyCouponUse && (
                        <CardOptionWrapper
                            selected={paymentMethod === "card"}
                            onClick={() => {
                                setPaymentMethod("card");
                            }}
                        >
                            <HeaderRow>
                                <PaymentMethodText>카드 결제</PaymentMethodText>
                            </HeaderRow>
                            {paymentMethod === "card" && (
                                <>
                                    {/* ── 기간 선택 */}
                                    <CardTermSelector>
                                        <TermOption selected={cardTerm === "1m"} onClick={() => setCardTerm("1m")}>
                                            <RadioButton
                                                type="radio"
                                                name="cardTerm"
                                                checked={cardTerm === "1m"}
                                                onChange={() => setCardTerm("1m")}
                                            />
                                            <OptionText>1개월 이용</OptionText>
                                            {props.isGroupPlan && (
                                                <DiscountBadge>
                                                    {Number(groupDiscountPolicy?.discount_rate)}% 할인
                                                </DiscountBadge>
                                            )}
                                        </TermOption>

                                        <TermOption selected={cardTerm === "3m"} onClick={() => setCardTerm("3m")}>
                                            <RadioButton
                                                type="radio"
                                                name="cardTerm"
                                                checked={cardTerm === "3m"}
                                                onChange={() => setCardTerm("3m")}
                                            />
                                            <OptionText>{longTermDiscounPolicy?.target_month}개월 이용</OptionText>
                                            <DiscountBadge>
                                                {Number(longTermDiscounPolicy?.discount_rate) +
                                                    (props.isGroupPlan
                                                        ? Number(groupDiscountPolicy?.discount_rate)
                                                        : 0)}
                                                % 할인
                                            </DiscountBadge>
                                        </TermOption>
                                    </CardTermSelector>

                                    {/* ── 요금 정보 박스 */}
                                    <CardInfoBox>
                                        <InfoRow>
                                            <GeneralText {...contentStyle}>
                                                월 이용 요금 {props.isGroupPlan && "(1명)"}
                                            </GeneralText>
                                            <GeneralText {...contentStyle}>
                                                {monthlyAmount.toLocaleString()}원
                                            </GeneralText>
                                        </InfoRow>

                                        <InfoRow style={{ paddingLeft: "8px" }}>
                                            <FeeSubText>기본 요금</FeeSubText>
                                            <FeeSubText>{plan.price?.toLocaleString()}원</FeeSubText>
                                        </InfoRow>

                                        <InfoRow style={{ paddingLeft: "8px" }}>
                                            <FeeSubText>
                                                장기 결제 할인 ({Number(longTermDiscounPolicy?.discount_rate)}%)
                                            </FeeSubText>
                                            <FeeSubText>
                                                {cardTerm === "3m" ? (
                                                    <>
                                                        -
                                                        {(
                                                            (plan?.price * longTermDiscounPolicy?.discount_rate) /
                                                            100
                                                        ).toLocaleString()}
                                                        원
                                                    </>
                                                ) : (
                                                    "해당없음"
                                                )}
                                            </FeeSubText>
                                        </InfoRow>

                                        {props.isGroupPlan && (
                                            <InfoRow style={{ paddingLeft: "8px" }}>
                                                <FeeSubText>
                                                    그룹 결제 할인 ({Number(longTermDiscounPolicy?.discount_rate)}%)
                                                </FeeSubText>
                                                <FeeSubText>
                                                    -
                                                    {(
                                                        (plan?.price * groupDiscountPolicy?.discount_rate) /
                                                        100
                                                    ).toLocaleString()}
                                                    원
                                                </FeeSubText>
                                            </InfoRow>
                                        )}
                                        {props.isGroupPlan && (
                                            <InfoRow>
                                                <GeneralText {...contentStyle}>멤버 수</GeneralText>
                                                <GeneralText {...contentStyle}>
                                                    {props.groupInfo.max_member} 명
                                                </GeneralText>
                                            </InfoRow>
                                        )}

                                        <InfoRow>
                                            <GeneralText {...contentStyle}>이용 기간</GeneralText>
                                            <GeneralText {...contentStyle}>
                                                {cardTerm === "1m" ? "1개월" : "3개월"}
                                            </GeneralText>
                                        </InfoRow>

                                        <Divider />

                                        <InfoRow>
                                            <GeneralText {...contentStyle} fontWeight="bold">
                                                결제 금액
                                            </GeneralText>
                                            <GeneralText {...contentStyle} fontWeight="bold">
                                                {paymentAmount.toLocaleString()}원
                                            </GeneralText>
                                        </InfoRow>
                                    </CardInfoBox>

                                    {/* ── 자동 연장 토글 */}
                                    <ToggleRow>
                                        <GeneralText {...contentStyle}>이용 기간 만료 후 자동 연장</GeneralText>
                                        <Checkbox
                                            checked={autoExtend}
                                            onChange={(e) => {
                                                setAutoExtend(e.target.checked);
                                            }}
                                            margin={"0 0 0 auto"}
                                        />
                                    </ToggleRow>
                                </>
                            )}
                        </CardOptionWrapper>
                    )}
                </OptionWrapper>
            </Wrapper>

            <SubInfoBox>
                <InfoListWrap>
                    <SubInfoItem>
                        <GeneralText {...listStyle}>표시된 모든 금액은 부가세(VAT)가 포함된 금액입니다.</GeneralText>
                    </SubInfoItem>
                    <SubInfoItem>
                        <GeneralText {...listStyle}>결제 완료 후 환불은 제공되지 않습니다.</GeneralText>
                    </SubInfoItem>
                </InfoListWrap>
            </SubInfoBox>

            <AbstractModalFooter
                leftBtnText={"취소"}
                rightBtnText={"결제하기"}
                leftOnClick={props.exitModal}
                rightOnClick={() => {
                    if (paymentMethod === "coupon") {
                        if (selectedCoupon === null || selectedCoupon === "") {
                            Alert("warn", "쿠폰 선택", "사용할 쿠폰을 선택해주세요.");
                            return;
                        }
                        couponUseMutation.mutate(
                            { number: selectedCoupon },
                            {
                                onSuccess: () => {
                                    Alert("info", "구독", "요금제의 이용 기간이 연장되었습니다.");
                                    props.exitModal();
                                    navigate("/payment");
                                },
                                onError: () => {
                                    Alert(
                                        "warn",
                                        "에러",
                                        "쿠폰 사용 중 오류가 발생했습니다. 쿠폰 정보를 다시 확인해주세요.",
                                    );
                                },
                            },
                        );
                    } else {
                        extendSubscription();
                    }
                }}
            />
            {showCouponRegisterModal && (
                <AbstractModal
                    modalTitle="Add Coupon"
                    width={480}
                    exitModal={(e) => setShowCouponRegisterModal(!showCouponRegisterModal)}
                >
                    <CouponRegisterModal exitModal={(e) => setShowCouponRegisterModal(!showCouponRegisterModal)} />
                </AbstractModal>
            )}
        </Container>
    );
}

export default PaymentExtendSubscribeModal;
